import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Link } from 'react-router-dom';

const DPA = () => {
  return (
    <Layout>
      <SEOHead
        title="Data Processing Agreement (DPA) - NoQ5.ai"
        description="The NoQ5 Data Processing Agreement covering GDPR Article 28 obligations, EU Standard Contractual Clauses, security measures and subprocessor management."
        canonicalUrl="https://noq5.ai/dpa"
      />
      <article className="container px-4 md:px-6 py-12 max-w-3xl mx-auto prose prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary">
        <header className="not-prose mb-10">
          <p className="text-sm text-muted-foreground">Effective date: 25 April 2026 · Version 1.0</p>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mt-2">Data Processing Agreement</h1>
          <p className="text-muted-foreground mt-3">
            This Data Processing Agreement ("DPA") supplements the Terms of Service between NoQ5.ai
            Technologies Ltd. ("Processor") and the Customer ("Controller") and applies whenever NoQ5
            processes personal data on Customer's behalf in scope of the GDPR or UK GDPR. A countersigned
            copy is available on request at{' '}
            <a href="mailto:batiste@noq5.ai">batiste@noq5.ai</a>.
          </p>
        </header>

        <h2>1. Subject matter and duration</h2>
        <p>
          NoQ5 processes personal data to provide the Service described in the Terms, for the duration of
          the subscription and any wind-down period.
        </p>

        <h2>2. Nature, purpose and categories</h2>
        <ul>
          <li><strong>Nature:</strong> hosting, summarisation, drafting and (with approval) writing of CRM updates.</li>
          <li><strong>Purpose:</strong> performance of the Service.</li>
          <li><strong>Data subjects:</strong> Customer's employees, customers, prospects and meeting participants.</li>
          <li><strong>Categories of data:</strong> business contact details, employment information, communication content (email, calls, meetings), CRM activity records.</li>
          <li><strong>Special categories:</strong> not intentionally processed; Customer must not submit them.</li>
        </ul>

        <h2>3. Processor obligations (Art. 28 GDPR)</h2>
        <ul>
          <li>Process personal data only on Controller's documented instructions, including the Terms and DPA;</li>
          <li>Ensure persons authorised to process data are bound by confidentiality;</li>
          <li>Implement the technical and organisational measures listed in Annex II;</li>
          <li>Engage subprocessors only as set out in Section 5;</li>
          <li>Assist the Controller with data-subject requests, DPIAs and security incident notifications;</li>
          <li>At Controller's choice, delete or return personal data at end of provision;</li>
          <li>Make available all information necessary to demonstrate compliance and allow audits (see Section 7).</li>
        </ul>

        <h2>4. Controller obligations</h2>
        <p>
          Controller warrants it has a lawful basis for the processing instructed, that any required notices
          and consents are in place, and that it will not transmit personal data to NoQ5 in violation of
          law.
        </p>

        <h2>5. Subprocessors</h2>
        <p>
          Controller grants general authorisation for NoQ5 to engage subprocessors. The current list is
          published at <Link to="/subprocessors">noq5.ai/subprocessors</Link>. NoQ5 will give at least 30
          days' notice of intended changes; Controller may object on reasonable data-protection grounds, in
          which case the parties will work in good faith to find a solution or terminate the affected
          Service for a pro-rata refund.
        </p>

        <h2>6. International transfers</h2>
        <p>
          Where NoQ5 transfers personal data outside the EEA / UK / Switzerland, the parties enter into the
          European Commission Standard Contractual Clauses 2021/914 (Module 2 controller-to-processor or
          Module 3 processor-to-processor as applicable), and the UK Addendum / Swiss FDPIC addendum where
          relevant. Annex I and Annex II of this DPA serve as the corresponding annexes of the SCCs.
        </p>

        <h2>7. Audits</h2>
        <p>
          NoQ5 will respond to security questionnaires and provide its most recent third-party reports
          (penetration test summary, SOC 2 once available) once per calendar year. On 30 days' written
          notice, Controller may conduct an on-site audit limited in scope and frequency, at Controller's
          cost, subject to confidentiality and to a mutually-agreed schedule that does not disrupt
          operations.
        </p>

        <h2>8. Personal data breach</h2>
        <p>
          NoQ5 will notify Controller without undue delay and in any event within 72 hours of becoming aware
          of a personal data breach affecting Controller's data, with all information available to assist
          Controller in meeting its own notification obligations.
        </p>

        <h2>9. Liability</h2>
        <p>
          The liability provisions of the Terms apply equally to claims under this DPA. Nothing in this DPA
          excludes the rights of data subjects under the GDPR.
        </p>

        <h2>Annex I — Description of processing</h2>
        <p>See Sections 1-2 above. Frequency: continuous.</p>

        <h2>Annex II — Security measures</h2>
        <p>
          See <Link to="/security">noq5.ai/security</Link> for the full description of technical and
          organisational measures, including encryption, access control, logging, vulnerability management,
          and incident response.
        </p>

        <h2>Annex III — Subprocessors</h2>
        <p>
          The current list and locations are published at{' '}
          <Link to="/subprocessors">noq5.ai/subprocessors</Link>.
        </p>
      </article>
    </Layout>
  );
};

export default DPA;
